import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { authApi } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { getApiErrorMessage } from '@/utils/apiError';

type State = 'loading' | 'success' | 'already' | 'error';

/// Phase 3.Z — handles /verify-email?userId=…&token=… landing. Posts the
/// pair to backend on mount. Non-blocking flow: nothing else gates the
/// owner if this fails — the banner just stays up.
export function VerifyEmailPage() {
  const [search] = useSearchParams();
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const { markEmailConfirmed } = useAuthStore();
  const [state, setState] = useState<State>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const userId = search.get('userId') ?? '';
  const token = search.get('token') ?? '';

  useEffect(() => {
    if (!userId || !token) {
      setState('error');
      setErrorMessage(isArabic ? 'الرابط غير صالح' : 'Invalid link');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.verifyEmail(userId, token);
        if (cancelled) return;
        if (res.alreadyConfirmed) {
          setState('already');
        } else {
          setState('success');
          markEmailConfirmed();
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setState('error');
        setErrorMessage(getApiErrorMessage(err, isArabic ? 'فشل التحقق' : 'Verification failed'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  const text = {
    titleLoading: isArabic ? 'جاري التحقق...' : 'Verifying...',
    titleSuccess: isArabic ? 'تم تأكيد بريدك' : 'Email verified',
    titleAlready: isArabic ? 'البريد مؤكد بالفعل' : 'Email already verified',
    titleError: isArabic ? 'تعذّر التحقق' : 'Could not verify',
    bodySuccess: isArabic ? 'يمكنك الآن المتابعة إلى لوحة التحكم.' : 'You can continue to your dashboard now.',
    bodyAlready: isArabic ? 'لا حاجة لإعادة التحقق.' : 'No need to verify again.',
    goToDashboard: isArabic ? 'الذهاب إلى لوحة التحكم' : 'Go to dashboard',
    goToLogin: isArabic ? 'تسجيل الدخول' : 'Sign in',
  };

  return (
    <div className="space-y-6 text-center">
      {state === 'loading' && (
        <>
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
          <h2 className="text-xl font-bold">{text.titleLoading}</h2>
        </>
      )}
      {state === 'success' && (
        <>
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <h2 className="text-xl font-bold">{text.titleSuccess}</h2>
          <p className="text-muted-foreground text-sm">{text.bodySuccess}</p>
          <Link to="/business" className="inline-block px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium">
            {text.goToDashboard}
          </Link>
        </>
      )}
      {state === 'already' && (
        <>
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <h2 className="text-xl font-bold">{text.titleAlready}</h2>
          <p className="text-muted-foreground text-sm">{text.bodyAlready}</p>
          <Link to="/business" className="inline-block px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium">
            {text.goToDashboard}
          </Link>
        </>
      )}
      {state === 'error' && (
        <>
          <XCircle className="mx-auto h-12 w-12 text-rose-500" />
          <h2 className="text-xl font-bold">{text.titleError}</h2>
          <p className="text-muted-foreground text-sm">{errorMessage}</p>
          <Link to="/admin-login" className="inline-block px-6 py-3 rounded-lg border border-border font-medium">
            {text.goToLogin}
          </Link>
        </>
      )}
    </div>
  );
}
